import { array, compactFlatten, isString, object } from '@art-suite/art-core-ts';
import { getUniqueId } from '../commonLib';
import { ScanInlineProduct } from '../CommonScanSchemas';
import { pureFunctionalMap, pureFunctionalMerge } from '../pureFunctionalLib';
import {
  getScanTemplateChildren,
  SCAN_TEMPLATE_CHILDREN_FIELD_NAMES_BY_DEPTH,
  setScanTemplateChildren,
  validateScanTemplateDataStructureRecursionDepth,
} from './ScanTemplateDepthLib';
import {
  ScanTemplateGroup,
  ScanTemplateLineItem,
  ScanTemplateOption,
  ScanTemplatePart,
  ScanTemplatePartContext,
  ScanTemplatePartType,
  ScanTemplateRoot,
  ScanTemplateSkuOption,
  ScanTemplateTask,
} from './ScanTemplateSchemas';

// **************************************************************
// Scan TEMPLATE GETTERS
// **************************************************************
export const getSkuIdsFromSkuOption = (skuOption: ScanTemplateSkuOption): string[] => {
  if (isString(skuOption)) return [skuOption];
  return skuOption.skus ?? [];
};

/**
 * The product id a sku option names. A template may store the bare OrgProduct id or the whole
 * inline product; either way this is the id a line item's `skuId` takes and the id to fetch the
 * live OrgProduct by.
 */
export const getSkuIdFromSkuOption = (skuOption: ScanTemplateSkuOption): string => isString(skuOption) ? skuOption : skuOption.id;

/** The product a sku option carries inline, for templates that store the whole product rather than its id alone. */
export const getInlineProductFromSkuOption = (skuOption: ScanTemplateSkuOption): ScanInlineProduct | undefined => isString(skuOption) ? undefined : skuOption;

export const getTaskTemplatesForGroup = (scanTemplateRoot: ScanTemplateRoot, groupTemplate: ScanTemplateGroup): ScanTemplateTask[] => {
  const { taskTemplatesById } = scanTemplateRoot;
  const tasksSelectedById = taskTemplatesById && groupTemplate.taskTemplateIds?.map(
    taskTemplateId => taskTemplatesById[taskTemplateId],
  ).filter(v => !!v);

  const inlineTasks = groupTemplate.taskTemplates;
  if (tasksSelectedById && inlineTasks) return [...tasksSelectedById, ...inlineTasks];
  if (tasksSelectedById) return tasksSelectedById;
  if (inlineTasks) return inlineTasks;
  return [];
};

export const getScanTemplateGroup = (scanTemplateRoot: ScanTemplateRoot, groupId: string): ScanTemplateGroup => {
  const groupTemplate = getScanTemplateGroupIfExists(scanTemplateRoot, groupId);
  if (!groupTemplate) throw new Error(`Group template not found for groupId: ${groupId}`);
  return groupTemplate;
};

export const getScanTemplateGroupIfExists = (scanTemplateRoot: ScanTemplateRoot, groupId?: string): ScanTemplateGroup | undefined =>
  groupId ? scanTemplateRoot.groupTemplates?.find(groupTemplate => groupTemplate.id === groupId) : undefined;

export const getAllScanTaskTemplatesForGroup = (scanTemplateRoot: ScanTemplateRoot, scanTemplateGroup: ScanTemplateGroup): ScanTemplateTask[] => [
  ...(scanTemplateGroup.taskTemplates || []), // de-normalized tasks
  ...(scanTemplateGroup.taskTemplateIds || []).map(taskId => scanTemplateRoot.taskTemplatesById?.[taskId]).filter(v => !!v), // normalized tasks
];

export const getScanTemplateTaskIfExists = (
  scanTemplateRoot: ScanTemplateRoot,
  scanTemplateGroup: ScanTemplateGroup | undefined,
  taskId?: string,
): ScanTemplateTask | undefined =>
  taskId
    ? scanTemplateRoot.taskTemplatesById?.[taskId] // normalized tasks
      ?? scanTemplateGroup?.taskTemplates?.find(taskTemplate => taskTemplate.id === taskId) // de-normalized tasks
    : undefined;

export const getScanTemplateTask = (scanTemplateRoot: ScanTemplateRoot, scanTemplateGroup: ScanTemplateGroup | undefined, taskId: string): ScanTemplateTask => {
  const taskTemplate = getScanTemplateTaskIfExists(scanTemplateRoot, scanTemplateGroup, taskId);
  if (!taskTemplate) throw new Error(`Task template not found for taskId: ${taskId}`);
  return taskTemplate;
};

export const getScanTemplateOptionIfExists = (scanTemplateTask?: ScanTemplateTask, resolutionOptionId?: string): ScanTemplateOption | undefined =>
  resolutionOptionId && scanTemplateTask
    ? scanTemplateTask.optionTemplates?.find(resolutionOptionTemplate => resolutionOptionTemplate.id === resolutionOptionId)
    : undefined;

export const getScanTemplateOption = (scanTemplateTask: ScanTemplateTask, resolutionOptionId: string): ScanTemplateOption => {
  const resolutionOptionTemplate = getScanTemplateOptionIfExists(scanTemplateTask, resolutionOptionId);
  if (!resolutionOptionTemplate) throw new Error(`Resolution option template not found for resolutionOptionId: ${resolutionOptionId}`);
  return resolutionOptionTemplate;
};

export const getScanTemplateLineItemIfExists = (scanTemplateOption?: ScanTemplateOption, lineItemId?: string): ScanTemplateLineItem | undefined =>
  lineItemId && scanTemplateOption
    ? scanTemplateOption.lineItemTemplates?.find(lineItemTemplate => lineItemTemplate.id === lineItemId)
    : undefined;

export const getScanTemplateLineItem = (scanTemplateResolutionOption: ScanTemplateOption, lineItemId: string): ScanTemplateLineItem => {
  const lineItemTemplate = getScanTemplateLineItemIfExists(scanTemplateResolutionOption, lineItemId);
  if (!lineItemTemplate) throw new Error(`Line item template not found for lineItemId: ${lineItemId}`);
  return lineItemTemplate;
};

const getScanTemplatePartHelperTaskBase = (
  scanTemplateTask: ScanTemplateTask,
  scanTemplatePartContext: ScanTemplatePartContext,
): ScanTemplatePart | undefined => {
  const { optionId, lineItemId } = scanTemplatePartContext;
  if (!optionId) return scanTemplateTask;
  const optionTemplate = scanTemplateTask.optionTemplates?.find(optionTemplate => optionTemplate.id === optionId);
  if (!optionTemplate) return undefined;
  if (!lineItemId) return optionTemplate;
  const lineItemTemplate = optionTemplate.lineItemTemplates?.find(lineItemTemplate => lineItemTemplate.id === lineItemId);
  if (!lineItemTemplate) return undefined;
  return lineItemTemplate;
};

export const getScanTemplatePartType = (context: ScanTemplatePartContext): ScanTemplatePartType => {
  if (context.lineItemId) return 'lineItemTemplate';
  if (context.optionId) return 'optionTemplate';
  if (context.taskId) return 'taskTemplate';
  if (context.groupId) return 'groupTemplate';
  return 'scanTemplate';
};

export const getScanTemplatePart = (scanTemplateRoot: ScanTemplateRoot, scanTemplatePartContext: ScanTemplatePartContext): ScanTemplatePart | undefined => {
  const { groupId, taskId } = scanTemplatePartContext;
  let scanTemplateTask: ScanTemplateTask | undefined = undefined;
  if (taskId && (scanTemplateTask = scanTemplateRoot.taskTemplatesById?.[taskId])) {
    return getScanTemplatePartHelperTaskBase(scanTemplateTask, scanTemplatePartContext);
  }
  if (!groupId) return scanTemplateRoot;
  const groupTemplate = scanTemplateRoot.groupTemplates?.find(group => group.id === groupId);
  if (!groupTemplate) return undefined;
  if (!taskId) return groupTemplate;

  // @deprecated denormalized template support
  scanTemplateTask = groupTemplate.taskTemplates?.find(task => task.id === taskId);
  if (!scanTemplateTask) return undefined;
  return getScanTemplatePartHelperTaskBase(scanTemplateTask, scanTemplatePartContext);
};

export const getChildTemplate = (
  scanTemplateRoot: ScanTemplateRoot | undefined,
  parentTemplatePart: ScanTemplatePart,
  depth: number,
  templateScanPartId: string,
): ScanTemplatePart => {
  let result: ScanTemplatePart | undefined = undefined;
  switch (depth) {
    case 0: // scanTemplateRoot, so get groupTemplate
      result = scanTemplateRoot!.groupTemplates?.find(group => group.id === templateScanPartId);
      break;
    case 1: // scanTemplateGroup, so get taskTemplate
      result = scanTemplateRoot!.taskTemplatesById?.[templateScanPartId]
        ?? (parentTemplatePart as ScanTemplateGroup).taskTemplates?.find(task => task.id === templateScanPartId);
      break;
    case 2: // scanTemplateTask, so get optionTemplate
      result = (parentTemplatePart as ScanTemplateTask).optionTemplates?.find(option => option.id === templateScanPartId);
      break;
    case 3: // scanTemplateOption, so get lineItemTemplate
      result = (parentTemplatePart as ScanTemplateOption).lineItemTemplates?.find(lineItem => lineItem.id === templateScanPartId);
      break;
    default: // scanTemplateLineItem as no children, so return undefined
      throw new Error(`Can't get child-template for LineItem--LineItems have no children - depth: ${depth} (parentTemplatePart: ${parentTemplatePart.id})`);
  }
  if (!result) {
    throw new Error(`Template part not found for templateScanPartId: ${templateScanPartId} at depth: ${depth} (parentTemplatePart: ${parentTemplatePart.id})`);
  }
  return result;
};

export const getTemplateGroup = (template: ScanTemplateRoot, groupId: string): ScanTemplateGroup | undefined =>
  template.groupTemplates?.find(g => g.id === groupId);

export const getTemplateTask = (template: ScanTemplateRoot, groupId: string | undefined, taskId: string): ScanTemplateTask | undefined => {
  const group = groupId ? getTemplateGroup(template, groupId) : undefined;
  // instead of calling getScanTemplateTask (which throws an error when things are not found)
  // we will just return undefined here too keep the tests happy and backward compatible
  return template.taskTemplatesById?.[taskId] ?? group?.taskTemplates?.find(t => t.id === taskId);
};

export const getTemplateLineItem = (
  template: ScanTemplateRoot,
  groupId: string,
  taskId: string,
  resolutionOptionId: string,
  lineItemId: string,
): ScanTemplateLineItem | undefined => {
  const resolutionOption = getTemplateResolutionOption(template, groupId, taskId, resolutionOptionId);
  return resolutionOption?.lineItemTemplates?.find(li => li.id === lineItemId);
};

export const getAllScanTemplateTrades = (scanTemplateRoot: ScanTemplateRoot): string[] => {
  const trades = new Set<string>();
  getAllTaskTemplates(scanTemplateRoot).forEach(task => {
    if (task.trade) {
      trades.add(task.trade);
    }
  });
  return Array.from(trades);
};

export const getTemplateResolutionOption = (
  template: ScanTemplateRoot,
  groupId: string | undefined,
  taskId: string,
  resolutionOptionId: string,
): ScanTemplateOption | undefined => {
  const task = getTemplateTask(template, groupId, taskId);
  return task?.optionTemplates?.find(r => r.id === resolutionOptionId);
};

const getTemplateWithoutTimestampsRecursively = (templatePart: ScanTemplatePart, recursionDepth: number): ScanTemplatePart => {
  validateScanTemplateDataStructureRecursionDepth(recursionDepth);
  const children = getScanTemplateChildren(templatePart, recursionDepth);
  const { createdAt, updatedAt, ...rest } = templatePart;
  const result: any = rest;
  if (children?.length > 0) {
    setScanTemplateChildren(result, children.map(child => getTemplateWithoutTimestampsRecursively(child, recursionDepth + 1)), recursionDepth);
  } else delete result[SCAN_TEMPLATE_CHILDREN_FIELD_NAMES_BY_DEPTH[recursionDepth]];
  return result;
};

/**
 * Returns a copy of the templatePart with the timestamps removed
 * @param template
 * @returns
 */
export const getTemplateWithoutTimestamps = (template: ScanTemplatePart): ScanTemplatePart => getTemplateWithoutTimestampsRecursively(template, 0);

export const getAllNormalizedTaskTemplates = (scanTemplateRoot: ScanTemplateRoot): ScanTemplateTask[] => array(scanTemplateRoot.taskTemplatesById);

export const getAllDenormalizedTaskTemplates = (scanTemplateRoot: ScanTemplateRoot): ScanTemplateTask[] =>
  compactFlatten(array(scanTemplateRoot.groupTemplates, (group: ScanTemplateGroup) => group.taskTemplates));

export const getAllTaskTemplates = (scanTemplateRoot: ScanTemplateRoot): ScanTemplateTask[] => [
  ...getAllNormalizedTaskTemplates(scanTemplateRoot),
  ...getAllDenormalizedTaskTemplates(scanTemplateRoot),
];

export const getAllOptionTemplates = (scanTemplateRoot: ScanTemplateRoot): ScanTemplateOption[] =>
  compactFlatten(getAllTaskTemplates(scanTemplateRoot).map(task => task.optionTemplates));

export const getAllLineItemTemplates = (scanTemplateRoot: ScanTemplateRoot): ScanTemplateLineItem[] =>
  compactFlatten(getAllOptionTemplates(scanTemplateRoot).map(option => option.lineItemTemplates));

/**
 * Returns all the scan template parts in the template a as a flattened array. The parts are not modified in any way; each part still has its children.
 *
 * Note: DO NOT MODIFY THE RESULT OF THIS FUNCTION. This is a pure functional operation, parts of the original scanTemplateRoot are returned directly.
 * @param scanTemplateRoot
 * @returns
 */
export const getAllScanTemplateParts = (scanTemplateRoot: ScanTemplateRoot): ScanTemplatePart[] =>
  compactFlatten([
    scanTemplateRoot,
    scanTemplateRoot.groupTemplates,
    getAllTaskTemplates(scanTemplateRoot),
    getAllOptionTemplates(scanTemplateRoot),
    getAllLineItemTemplates(scanTemplateRoot),
  ]);

/**
 * Returns the most recent updatedAt (unix ms) across every part of the template, including overrides.
 * The root's own updatedAt only changes when root-level fields are edited, so it misses edits to
 * groups, tasks, options, and line items (e.g. a task's GL code).
 * @param scanTemplateRoot
 * @returns undefined when no part has an updatedAt
 */
export const getScanTemplateLastUpdatedAt = (scanTemplateRoot: ScanTemplateRoot): number | undefined => {
  let lastUpdatedAt: number | undefined;
  const consider = (updatedAt: number | undefined) => {
    if (updatedAt != null && (lastUpdatedAt == null || updatedAt > lastUpdatedAt)) lastUpdatedAt = updatedAt;
  };
  getAllScanTemplateParts(scanTemplateRoot).forEach(scanPart => {
    consider(scanPart.updatedAt);
    array(scanPart.overrides).forEach(override => consider(override.updatedAt));
  });
  return lastUpdatedAt;
};

/**
 * Returns all the sku id options in the template as a flattened array.
 *
 * Note: DO NOT MODIFY THE RESULT OF THIS FUNCTION. This is a pure functional operation, parts of the original scanTemplateRoot are returned directly.
 * @param scanTemplateRoot
 * @returns
 */
export const getAllSkuIdOptions = (scanTemplateRoot: ScanTemplateRoot): ScanTemplateSkuOption[] =>
  compactFlatten(getAllScanTemplateParts(scanTemplateRoot).map(scanPart => scanPart.skuIdOptions));

// ************************************************************************
// clone with new Ids
// ************************************************************************

const cloneScanTemplatePartWithNewIdsRecursively = (template: ScanTemplatePart, recursionDepth: number): ScanTemplatePart => {
  validateScanTemplateDataStructureRecursionDepth(recursionDepth);
  const result: any = { ...template, id: getUniqueId() };
  const children = getScanTemplateChildren(template, recursionDepth);

  if (children?.length > 0) {
    setScanTemplateChildren(result, children.map(child => cloneScanTemplatePartWithNewIdsRecursively(child, recursionDepth + 1)), recursionDepth);
  }

  if (template.overrides) {
    result.overrides = object(template.overrides, override => ({ ...override, id: getUniqueId() }));
  }

  return result;
};

const getTemplateRootWithClonedNormalizedTasks = (template: ScanTemplateRoot): ScanTemplateRoot => {
  if (!template.taskTemplatesById) return template; // not normalized, nothing to do
  const taskReIdMap: Record<string, string> = {};
  const newTaskTemplatesById: Record<string, ScanTemplateTask> = {};
  for (const taskId in template.taskTemplatesById ?? {}) {
    const newTask = cloneScanTemplatePartWithNewIdsRecursively(template.taskTemplatesById[taskId], 2);
    const newTaskId = newTask.id;
    taskReIdMap[taskId] = newTaskId;
    newTaskTemplatesById[newTaskId] = newTask;
  }
  return {
    ...template,
    groupTemplates: template.groupTemplates?.map(group => ({
      ...group,
      taskTemplateIds: group.taskTemplateIds?.map(taskId => taskReIdMap[taskId]),
    })),
    taskTemplatesById: newTaskTemplatesById,
  };
};

export const cloneScanTemplateDataWithNewIds = (template: ScanTemplateRoot): ScanTemplateRoot =>
  getTemplateRootWithClonedNormalizedTasks(cloneScanTemplatePartWithNewIdsRecursively(template, 0));

export const getAllExternalSkuIds = (template: ScanTemplateRoot): string[] => {
  const skuIds: string[] = [];
  for (const group of template.groupTemplates ?? []) {
    for (const task of group.taskTemplates ?? []) {
      for (const option of task.optionTemplates ?? []) {
        for (const lineItem of option.lineItemTemplates ?? []) {
          skuIds.push(...(lineItem.skuIdOptions?.filter(isString) ?? []));
        }
      }
    }
  }
  return skuIds;
};

// ************************************************************************
// get template with inline products
// ************************************************************************

const getTemplatePartWithInlineProducts = (
  templatePart: ScanTemplatePart,
  inlineProductsById: Record<string, ScanInlineProduct>,
  depth: number = 0,
): ScanTemplatePart => {
  const childrenName = SCAN_TEMPLATE_CHILDREN_FIELD_NAMES_BY_DEPTH[depth];
  const children: ScanTemplatePart[] | undefined = childrenName ? (templatePart as any)[childrenName] : undefined;
  const skuIdOptions = pureFunctionalMap(templatePart.skuIdOptions, skuIdOption => isString(skuIdOption) ? inlineProductsById[skuIdOption] : skuIdOption);
  const newChildren = pureFunctionalMap(children, child => getTemplatePartWithInlineProducts(child, inlineProductsById, depth + 1));
  let taskTemplatesById: Record<string, ScanTemplateTask> | undefined;
  if (depth === 0 && (templatePart as ScanTemplateRoot).taskTemplatesById) {
    taskTemplatesById = pureFunctionalMerge(
      (templatePart as ScanTemplateRoot).taskTemplatesById,
      object(
        (templatePart as ScanTemplateRoot).taskTemplatesById,
        (taskTemplate): ScanTemplateTask => getTemplatePartWithInlineProducts(taskTemplate, inlineProductsById, 2),
      ),
    );
  }

  return pureFunctionalMerge(templatePart, {
    skuIdOptions,
    [childrenName]: newChildren,
    taskTemplatesById,
  } as Partial<ScanTemplatePart>);
};

export const getTemplateWithInlineProducts = (template: ScanTemplateRoot, inlineProductsById: Record<string, ScanInlineProduct>): ScanTemplateRoot =>
  getTemplatePartWithInlineProducts(template, inlineProductsById, 0) as ScanTemplateRoot;

export type TaskIdToGroupIdsMap = Record<string, ScanTemplateGroup[]>;

export const getTemplateTaskIdToGroupsMap = (template: ScanTemplateRoot): TaskIdToGroupIdsMap => {
  const result: TaskIdToGroupIdsMap = {};
  template.groupTemplates?.forEach(group => {
    group.taskTemplateIds?.forEach(taskId => {
      if (!result[taskId]) {
        result[taskId] = [];
      }
      result[taskId].push(group);
    });
  });
  return result;
};

// ************************************************************************
// Work with collections of tasks and groups
// ************************************************************************

/**
 * Returns the common values shared across all provided groups.
 * Only values that exist in all groups with the same name are included.
 * @param groups - Collection of ScanTemplateGroup objects to analyze
 * @returns ScanTemplateValues object containing only the values common to all groups
 */
export const getValuesCommonToAllGroups = (groups: ScanTemplateGroup[]): Record<string, any> => {
  if (!groups || groups.length === 0) return {};
  if (groups.length === 1) return groups[0].values || {};

  // Get the first group's values as the starting point
  const firstGroupValues = groups[0].values || {};
  const commonValues: Record<string, any> = {};

  // Check each value in the first group
  for (const [valueName, valueData] of Object.entries(firstGroupValues)) {
    let isCommonToAll = true;

    // Check if this value exists in all other groups
    for (let i = 1; i < groups.length; i++) {
      const otherGroupValues = groups[i].values || {};
      if (!(valueName in otherGroupValues)) {
        isCommonToAll = false;
        break;
      }
    }

    // If the value exists in all groups, include it in the result
    if (isCommonToAll) {
      commonValues[valueName] = valueData;
    }
  }

  return commonValues;
};

/**
 * Collect all groups that contain the specified task ID.
 * @param taskId The ID of the task to search for.
 * @param groups The list of groups to search through.
 * @returns A list of groups that contain the specified task.
 */
export const getAllGroupsThatContainTaskId = (
  taskId: string,
  groups: ScanTemplateGroup[],
): ScanTemplateGroup[] => groups.filter(group => group.taskTemplateIds?.includes(taskId)) || [];

/**
 * Map tasks by their names for easy lookup.
 * @param kaiTemplate The Kai template containing the tasks.
 * @returns A map of task names to their corresponding ScanTemplateTask objects.
 */
export const getTasksByName = (
  kaiTemplate: ScanTemplateRoot,
): { [taskName: string]: ScanTemplateTask } => {
  if (!kaiTemplate.taskTemplatesById || Object.keys(kaiTemplate.taskTemplatesById).length === 0) {
    return {};
  }

  const tasksByName: { [taskName: string]: ScanTemplateTask } = {};
  Object.entries(kaiTemplate.taskTemplatesById).forEach(([taskId, task]: [string, ScanTemplateTask]) => {
    if (!task.name) {
      console.warn(`Task ${taskId} in template1 has no name; skipping`);
      return;
    }
    if (tasksByName[task.name]) {
      console.warn(`Duplicate Task name ${task.name} in template1; skipping`);
      return;
    }
    tasksByName[task.name] = task;
  });
  return tasksByName;
};
